import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { DEFAULT_ERROR_MESSAGE } from '@/services/api';
import { todayDateKey } from '@features/quote/utils/dateKey';
import {
    confirmSmriqVehicle,
    fetchSmriqSchedulePreview,
    generateSmriqSchedule,
    type ConfirmSmriqVehiclePayload,
    type GenerateSmriqSchedulePayload,
} from '../services/smriq';
import { mapSmriqServices } from '../utils/mapSmriqServices';
import { boundSmriqServices, type SmriqServiceCountBounds } from '../utils/smriqServiceCountBounds';
import { isAutoMatch } from '../utils/isAutoMatch';
import { toLastCompletedServiceInput } from '../utils/toLastCompletedServiceInput';
import { toGenerateJourneyError } from '../utils/smriqGenerateError';
import { toJourneyError, type SmriqJourneyError } from '../utils/smriqJourneyError';
import type { ConfiguredService } from '../types/service.types';
import type {
    SmriqConfirmedVehicle,
    SmriqGeneratedService,
    SmriqGeneratedPlan,
    SmriqLastCompletedServiceSelection,
    SmriqOperationExclusion,
    SmriqPlanSettings,
    SmriqPricingVariantRecord,
    SmriqSchedulePreviewResult,
} from '../types/smriq.types';

export type { SmriqJourneyError };

const matchedIdFromEcho = (
    selection: SmriqLastCompletedServiceSelection | null | undefined,
    echo: SmriqGeneratedPlan['last_completed_service'],
): string | null =>
    isAutoMatch(selection) && typeof echo?.service_option_id === 'string' ? echo.service_option_id : null;

const IDLE_PREVIEW_STATUS = { loading: false, error: null };

export interface UseSmriqJourneyArgs {
    productId: string | undefined;
    sessionId: string | null;
    /** The snapshot taken against `sessionId`, or null when none survives for it. */
    confirmedVehicle: SmriqConfirmedVehicle | null;
    onSessionId: (sessionId: string | null) => void;
    onConfirmedVehicle: (vehicle: SmriqConfirmedVehicle | null) => void;
    onServices: (services: ConfiguredService[]) => void;
    /** The raw per-visit `items`/`declined_items` behind a successful map, for the
     *  inclusions display — never derived from `ConfiguredService`. */
    onGeneratedServices?: (
        services: SmriqGeneratedService[],
        pricingVariant?: SmriqPricingVariantRecord | null,
    ) => void;
    /** The settings a generate was sent with, handed back only once that generate succeeded and
     *  mapped: a failed one must not be recorded against the schedule still on the quote. */
    onPlanSettings?: (settings: SmriqPlanSettings) => void;
    /** The product's resolved min/max; the plan is rejected below min and trimmed to max. */
    serviceCountBounds?: SmriqServiceCountBounds;
}

interface GenerateVariables {
    payload: GenerateSmriqSchedulePayload;
    selection: SmriqLastCompletedServiceSelection | null | undefined;
}

export interface GeneratePlanInput {
    duration_months: number;
    include_mot_tests: boolean;
    recommended_operation_exclusions?: SmriqOperationExclusion[];
    last_completed_service?: SmriqLastCompletedServiceSelection | null;
    plan_start_date?: string;
}

// `onServices` and `onSessionId` are rebuilt on every configuredServices change,
// so they are held in refs and never re-subscribe a mutation callback.
export function useSmriqJourney({
    productId,
    sessionId,
    confirmedVehicle,
    onSessionId,
    onConfirmedVehicle,
    onServices,
    onGeneratedServices,
    onPlanSettings,
    serviceCountBounds,
}: UseSmriqJourneyArgs) {
    const onServicesRef = useRef(onServices);
    onServicesRef.current = onServices;
    const onGeneratedServicesRef = useRef(onGeneratedServices);
    onGeneratedServicesRef.current = onGeneratedServices;
    const onPlanSettingsRef = useRef(onPlanSettings);
    onPlanSettingsRef.current = onPlanSettings;
    const serviceCountBoundsRef = useRef(serviceCountBounds);
    serviceCountBoundsRef.current = serviceCountBounds;
    const onSessionIdRef = useRef(onSessionId);
    onSessionIdRef.current = onSessionId;
    const onConfirmedVehicleRef = useRef(onConfirmedVehicle);
    onConfirmedVehicleRef.current = onConfirmedVehicle;
    const confirmedVehicleRef = useRef(confirmedVehicle);
    confirmedVehicleRef.current = confirmedVehicle;

    // `reset()` drops the observer but never cancels the in-flight request: confirm,
    // generate and restart each open a run so a superseded success is discarded.
    const runRef = useRef(0);
    const closedRef = useRef(false);
    const startRun = useCallback(() => {
        runRef.current += 1;
        return { run: runRef.current };
    }, []);
    const isCurrentRun = useCallback(
        (run: number) => !closedRef.current && run === runRef.current,
        [],
    );

    // A success may hand back a replacement session for the same confirmed vehicle, so
    // the snapshot follows it rather than being orphaned against the superseded id.
    const applySessionId = useCallback((nextSessionId: string) => {
        const snapshot = confirmedVehicleRef.current;
        if (snapshot && snapshot.sessionId !== nextSessionId) {
            onConfirmedVehicleRef.current({ ...snapshot, sessionId: nextSessionId });
        }
        onSessionIdRef.current(nextSessionId);
    }, []);

    // A mutation's own onSuccess runs off the Mutation, not the observer, so it still
    // fires after unmount: close the window so a late write cannot reach another quote.
    useEffect(() => {
        closedRef.current = false;

        return () => {
            closedRef.current = true;
            runRef.current += 1;
        };
    }, [productId]);

    const [preview, setPreview] = useState<SmriqSchedulePreviewResult | null>(null);
    const [confirmedThisSession, setConfirmedThisSession] = useState(false);
    const [vehicleDirty, setVehicleDirty] = useState(false);
    // 'selection' when only the last-completed-service choice changed: that section has its own
    // Update, so the plan controls need not open for it.
    const [staleSource, setStaleSource] = useState<'selection' | 'other' | null>(null);
    const isStale = staleSource !== null;
    // True only for a generate fired in this browser session; a resumed quote's own
    // persisted settings are tracked separately (planSettingsKnownOrRestored).
    const [planSettingsKnown, setPlanSettingsKnown] = useState(false);
    const [mappingError, setMappingError] = useState<SmriqJourneyError | null>(null);
    // Set in the same handler that starts the chained preview, so no render sees a confirmed
    // session whose preview has not yet begun.
    const [previewAwaited, setPreviewAwaited] = useState(false);

    const previewRunRef = useRef(0);
    // Tracked off the mutation's own callbacks: a mount-time preview loses its observer across
    // a strict-mode remount, which would leave `isPending` reporting a finished request as live.
    const [previewStatus, setPreviewStatus] = useState<{
        loading: boolean;
        error: SmriqJourneyError | null;
    }>(IDLE_PREVIEW_STATUS);
    const [matchedServiceOptionId, setMatchedServiceOptionId] = useState<string | null>(null);

    // Chained by confirm, so it joins that run rather than opening one of its own.
    const previewMutation = useMutation({
        mutationFn: (vars: { sessionId: string; currentMileage: number; annualMileage: number }) =>
            fetchSmriqSchedulePreview(
                productId as string,
                vars.sessionId,
                vars.currentMileage,
                vars.annualMileage,
            ),
        onMutate: () => {
            previewRunRef.current += 1;
            setPreviewStatus({ loading: true, error: null });
            return { run: runRef.current, previewRun: previewRunRef.current };
        },
        onSuccess: (result, _vars, context) => {
            if (!isCurrentRun(context.run)) return;
            setPreview(result);
            applySessionId(result.service_plan_session_id);
        },
        onSettled: (_result, error, _vars, context) => {
            if (context && !closedRef.current && context.previewRun === previewRunRef.current) {
                setPreviewAwaited(false);
                setPreviewStatus({ loading: false, error: error ? toJourneyError(error) : null });
            }
        },
    });

    const generateMutation = useMutation({
        mutationFn: ({ payload }: GenerateVariables) =>
            generateSmriqSchedule(productId as string, payload),
        onMutate: startRun,
        onSuccess: (result, { payload, selection }, context) => {
            if (!isCurrentRun(context.run)) return;
            // Session advances even when the map fails: the retry must continue
            // against the replacement id, never the superseded one.
            applySessionId(result.service_plan_session_id);
            try {
                const kept = boundSmriqServices(
                    result.services,
                    isAutoMatch(selection),
                    serviceCountBoundsRef.current
                );
                const mapped = mapSmriqServices(kept);
                onServicesRef.current(mapped);
                onGeneratedServicesRef.current?.(kept, result.plan.pricing_variant ?? null);
                // Read back off the sent payload, so what is recorded is exactly what the
                // vendor priced; the selection is carried alongside because the wire payload
                // omits its label.
                onPlanSettingsRef.current?.({
                    duration_months: payload.duration_months,
                    include_mot_tests: payload.include_mot_tests,
                    plan_start_date: payload.plan_start_date,
                    last_completed_service: selection ?? null,
                });
                setStaleSource(null);
                setPlanSettingsKnown(true);
                setMappingError(null);
                setMatchedServiceOptionId(matchedIdFromEcho(selection, result.plan.last_completed_service));
            } catch (error) {
                // Surfaced via state, never sent on to setConfiguredServices: a
                // rejected map must not leave a partial plan on the quote.
                setMatchedServiceOptionId(null);
                setMappingError({
                    code: 'plan_mapping_failed',
                    message: error instanceof Error ? error.message : DEFAULT_ERROR_MESSAGE,
                });
            }
        },
    });

    const confirmMutation = useMutation({
        mutationFn: (payload: ConfirmSmriqVehiclePayload) =>
            confirmSmriqVehicle(productId as string, payload),
        onMutate: startRun,
        onSuccess: (result, payload, context) => {
            if (!isCurrentRun(context.run)) return;
            onSessionIdRef.current(result.service_plan_session_id);
            // What the vendor priced, so a remount prefills the confirmed values
            // rather than the quote's untouched current_status.
            onConfirmedVehicleRef.current({
                sessionId: result.service_plan_session_id,
                vrm: payload.vrm,
                currentMileage: payload.current_mileage,
                annualMileage: payload.annual_mileage,
            });
            setConfirmedThisSession(true);
            setVehicleDirty(false);
            setMatchedServiceOptionId(null);
            // The held preview describes the superseded session and mileage.
            setPreview(null);
            setPreviewAwaited(true);
            // Items and declines belong to the superseded session: left in place they
            // would ride the replacement session's first generate as exclusions.
            onGeneratedServicesRef.current?.([]);
            // The schedule on screen was priced against the superseded session; drop it so
            // the summary shows no stale plan/price while the replacement regenerates.
            onServicesRef.current([]);
            // With no plan on screen its duration/MOT describe nothing; a generate success
            // re-establishes this.
            setPlanSettingsKnown(false);
            // The new session supersedes any plan generated against the old one, so
            // only a generate success may clear this.
            setStaleSource('other');
            // A restart-then-reconfirm must not resurrect the previous attempt's
            // generate error: the panel would stay locked forever otherwise.
            generateMutation.reset();
            // Preview is chained with no dealer action; its failure is non-blocking
            // because generate needs only the session id, never the preview payload.
            previewMutation.mutate({
                sessionId: result.service_plan_session_id,
                currentMileage: payload.current_mileage,
                annualMileage: payload.annual_mileage,
            });
        },
    });

    const confirmVehicle = useCallback(
        (payload: ConfirmSmriqVehiclePayload) => confirmMutation.mutate(payload),
        [confirmMutation],
    );

    const generatePlan = useCallback(
        (input: GeneratePlanInput) => {
            if (!sessionId) return;
            setMappingError(null);

            const payload: GenerateSmriqSchedulePayload = {
                service_plan_session_id: sessionId,
                // Reuses the date the plan was originally generated with, so a resumed
                // regenerate reproduces the saved schedule rather than shifting it to today.
                plan_start_date: input.plan_start_date ?? todayDateKey(),
                // Unreachable in the shipped UI: Generate/Update is disabled whenever
                // needsReconfirm is true, which is exactly the state with no snapshot.
                annual_mileage: confirmedVehicleRef.current?.annualMileage ?? 0,
                duration_months: input.duration_months,
                include_mot_tests: input.include_mot_tests,
                recommended_operation_exclusions: input.recommended_operation_exclusions ?? [],
                last_completed_service: toLastCompletedServiceInput(input.last_completed_service),
            };

            generateMutation.mutate({ payload, selection: input.last_completed_service });
        },
        [sessionId, generateMutation],
    );

    const { mutate: runPreview } = previewMutation;
    const retryPreview = useCallback(() => {
        const snapshot = confirmedVehicleRef.current;
        if (!sessionId || !snapshot) return;
        setPreviewAwaited(true);
        runPreview({
            sessionId,
            currentMileage: snapshot.currentMileage,
            annualMileage: snapshot.annualMileage,
        });
    }, [sessionId, runPreview]);

    const markStale = useCallback(() => setStaleSource('other'), []);
    const markSelectionStale = useCallback(
        () => setStaleSource((previous) => previous ?? 'selection'),
        [],
    );

    // The session carries the confirmed vehicle, so an edited field only reaches
    // the vendor through another confirm; generation stays blocked until then.
    const markVehicleDirty = useCallback(() => {
        setVehicleDirty(true);
        setStaleSource('other');
    }, []);

    // Discards an in-progress edit (vehicle fields or plan controls) without changing the saved
    // schedule: the flags markVehicleDirty/markStale raised are cleared, back to the settled state.
    const discardEdit = useCallback(() => {
        setVehicleDirty(false);
        setStaleSource(null);
    }, []);

    // Restarts the journey without touching the still-saleable schedule already
    // in configuredServices.
    const restart = useCallback(() => {
        runRef.current += 1;
        previewRunRef.current += 1;
        setConfirmedThisSession(false);
        setVehicleDirty(false);
        setPreview(null);
        setPreviewAwaited(false);
        setPreviewStatus(IDLE_PREVIEW_STATUS);
        setStaleSource(null);
        setPlanSettingsKnown(false);
        setMappingError(null);
        setMatchedServiceOptionId(null);
        onSessionIdRef.current(null);
        onConfirmedVehicleRef.current(null);
        onGeneratedServicesRef.current?.([]);
        generateMutation.reset();
        previewMutation.reset();
        // `.reset` is stable per mutation; the mutation objects are new every
        // render and would defeat this memo.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [generateMutation.reset, previewMutation.reset]);

    const generateError =
        mappingError ?? (generateMutation.error ? toGenerateJourneyError(generateMutation.error) : null);

    // A session restored without its snapshot describes a confirmation this browser never
    // saw, so the values on screen are not the ones the vendor priced.
    const sessionUnverified = sessionId !== null && !confirmedVehicle && !confirmedThisSession;

    return {
        confirmVehicle,
        isConfirming: confirmMutation.isPending,
        confirmError: confirmMutation.error ? toJourneyError(confirmMutation.error) : null,
        preview,
        previewError: previewStatus.error,
        isPreviewLoading: previewStatus.loading,
        previewSettled: !previewAwaited,
        matchedServiceOptionId,
        retryPreview,
        // A resumed quote arrives with its sessionId restored; no vendor call is
        // made to "confirm" that, it is already confirmed.
        confirmed: sessionId !== null || confirmedThisSession,
        generatePlan,
        isGenerating: generateMutation.isPending,
        generateError,
        isStale,
        planControlsStale: staleSource === 'other',
        planSettingsKnown,
        markStale,
        markSelectionStale,
        needsReconfirm: vehicleDirty || sessionUnverified,
        markVehicleDirty,
        discardEdit,
        restart,
    };
}
